<template>
  <div id="applicationManagement">
    <div class="container">
      <div class="header-section">
        <h1 class="page-title">Student Application Management</h1>
      </div>

      <div v-if="listCount > 0" class="application-content">
        <!-- Student Photo Section -->
        <StudentPhotoCard :image-url="imageUrl" @photo-uploaded="file => handleFileUpload([file], 'stuIMG')" />

        <!-- Main Application Content -->
        <div class="application-main-card">
          <!-- Status and Print Section -->
          <ApplicationHeader :enrol-status="enrolStatus" :application-form-id="applicationFormID" :api-host="apiHost" @send-notification="sendNotification" />

          <!-- Timeline -->
          <ApprovalTimeline :application-status="enrolStatus" :approver-list="approverList" :school-id="form.APF_ApplicationSchool" />

          <!-- Form Sections -->
          <div class="form-sections">
            <!-- Student Details -->
            <FormSection title="Student Details">
              <div class="form-grid">
                <FormField v-for="field in studentFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" :school-list="schoolList" />
              </div>
            </FormSection>

            <!-- Siblings -->
            <FormSection v-if="form.siblings.length > 0" title="Siblings">
              <SiblingsSection :siblings="form.siblings" />
            </FormSection>

            <!-- Parent Details -->
            <FormSection v-if="form.APFP_FatherFirstName" title="Father Details">
              <div class="form-grid">
                <FormField v-for="field in fatherFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>

            <FormSection v-if="form.APFP_MotherFirstName" title="Mother Details">
              <div class="form-grid">
                <FormField v-for="field in motherFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>

            <!-- Guardian Details -->
            <FormSection v-if="form.APFP_LegalGuardianName" title="Local Guardian Details">
              <div class="form-grid">
                <FormField v-for="field in guardianFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>

            <!-- Emergency Contacts -->
            <FormSection title="Emergency Contact">
              <EmergencyContactsSection :form="form" :can-edit="!cantEdit" />
            </FormSection>

            <!-- Correspondence -->
            <FormSection title="Correspondence">
              <div class="form-grid">
                <FormField v-for="field in correspondenceFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>

            <!-- Billing -->
            <FormSection title="Billing Name & Address for Tuition Fees (if different from above)">
              <div class="form-grid">
                <FormField v-for="field in billingFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>

            <!-- Supportive Information -->
            <SupportiveInformationSection :answers="form.answers" :is-preschool="isPreschool" />

            <!-- Referral Agent -->
            <FormSection title="For Authorised Representative">
              <div class="form-grid">
                <FormField v-for="field in referralFields" :key="field.key" :field="field" :form="form" :can-edit="!cantEdit" />
              </div>
            </FormSection>
          </div>

          <!-- Attachments -->
          <AttachmentsSection :attachment-list="attachmentList" @file-uploaded="handleFileUpload" />

          <!-- Request Changes -->
          <RequestChangesSection v-model="requestEditComment" @request-changes="requestApplicantEdits" />

          <!-- Kagami Section -->
          <KagamiSection
            :form="form"
            :parent-liaison-list="parentLiaisonList"
            :student-index-number="StudentIndexNumber"
            :academic-year="academicYear"
            :can-edit="!cantEdit"
          />

          <!-- Comments -->
          <CommentsSection :form="form" />

          <!-- Action Buttons -->
          <ActionButtons
            :list-count="listCount"
            :enable-approve-reject="enableApproveRejectButton"
            :enrol-status="enrolStatus"
            :current-apmd-lvl="currentAPMD_Lvl"
            :student-id="form.APF_FK_Student_ID"
            :action-in-progress="isApprovalActionInProgress"
            :has-auth-user-actioned="hasAuthUserActioned"
            @approve="approveClick"
            @reject="rejectClick"
            @delete="deleteClick"
            @save="saveForm"
            @create-student="createStudent"
            @delete-student="deleteStudent"
          />

          <!-- Rejection Action Buttons -->
          <RejectionActionButtons
            v-if="enrolStatus === 'Rejected' && showRejectionActionButtons"
            @approve-rejection="handleApproveRejection"
            @decline-rejection="handleDeclineRejection"
          />
        </div>
      </div>

      <!-- Empty State -->
      <EmptyState v-else />
    </div>

    <!-- Modals -->
    <RejectModal ref="rejectModal" v-model="inputRejectReason" @confirm="confirmReject" />

    <ApproveModal ref="approveModal" @confirm="confirmApprove" />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import DataSource from '../../data/datasource'
import { lang } from '../../locale'
import { isPreschoolApplication } from '@/pages/application/const/applicationConst'
import { getSchoolSession } from '@/helper/util_custom'
import StudentPhotoCard from './components/StudentPhotoCard.vue'
import ApplicationHeader from './components/ApplicationHeader.vue'
import ApprovalTimeline from './components/ApprovalTimeline.vue'
import FormSection from './components/FormSection.vue'
import FormField from './components/FormField.vue'
import SiblingsSection from './components/SiblingsSection.vue'
import EmergencyContactsSection from './components/EmergencyContactsSection.vue'
import SupportiveInformationSection from './components/SupportiveInformationSection.vue'
import AttachmentsSection from './components/AttachmentsSection.vue'
import RequestChangesSection from './components/RequestChangesSection.vue'
import KagamiSection from './components/KagamiSection.vue'
import CommentsSection from './components/CommentsSection.vue'
import ActionButtons from './components/ActionButtons.vue'
import EmptyState from './components/EmptyState.vue'
import RejectModal from './components/RejectModal.vue'
import ApproveModal from './components/ApproveModal.vue'
import RejectionActionButtons from './components/RejectionActionButtons.vue'

import {
  studentFieldsConfig,
  fatherFieldsConfig,
  motherFieldsConfig,
  guardianFieldsConfig,
  correspondenceFieldsConfig,
  billingFieldsConfig,
  referralFieldsConfig,
} from './config/formFields'

export default {
  name: 'ApplicationManagement',

  components: {
    StudentPhotoCard,
    ApplicationHeader,
    ApprovalTimeline,
    FormSection,
    FormField,
    SiblingsSection,
    EmergencyContactsSection,
    SupportiveInformationSection,
    AttachmentsSection,
    RequestChangesSection,
    KagamiSection,
    CommentsSection,
    ActionButtons,
    EmptyState,
    RejectModal,
    ApproveModal,
    RejectionActionButtons,
  },

  data() {
    return {
      authUserEmail: Cookies.get('userEmailSession'),
      apiHost: process.env.VUE_APP_ROOT_API,
      form: this.initializeForm(),
      approverList: [],
      attachmentList: [],
      inputRejectReason: '',
      applicationFormID: '',
      enrolStatus: '',
      imageUrl: '',
      listCount: 0,
      currentAPFID: '',
      schoolList: [],
      requestEditComment: '',
      parentLiaisonList: [],
      academicYear: {},
      StudentIndexNumber: '',
      isPreschool: false,
      isApprovalActionInProgress: false,
    }
  },

  computed: {
    cantEdit() {
      return 'darren.li@etonhouse.com.sg' !== this.authUserEmail && this.currentAPMD_Lvl > 1
    },

    enableApproveRejectButton() {
      const emails = ['welson.tan@etonhouse.com.sg', ...this.approverList.map(x => x.email).flat()]
      return emails.length > 1 && this.enrolStatus === 'Pending' && emails.includes(this.authUserEmail)
    },

    // Approvers who already gave a decision cannot approve/reject/delete again
    hasAuthUserActioned() {
      return this.approverList.some(x => {
        const emails = Array.isArray(x.email) ? x.email : [x.email]
        return emails.includes(this.authUserEmail) && ['Approved', 'Rejected'].includes(x.status)
      })
    },

    showRejectionActionButtons() {
      const emails = [
        'darren.li@etonhouse.com.sg',
        ...this.approverList
          .filter(x => x.level == 99)
          .map(x => x.email)
          .flat(),
      ]
      return emails.length > 1 && this.enrolStatus === 'Rejected' && emails.includes(this.authUserEmail)
    },

    currentAPMD_ID() {
      return this.approverList.find(x => x.status === 'Pending')?.apmdId
    },

    currentAPMD_Lvl() {
      return Number(this.approverList.find(x => x.status === 'Pending')?.level ?? 0)
    },

    // Form field configurations
    studentFields() {
      return studentFieldsConfig
    },

    fatherFields() {
      return fatherFieldsConfig
    },

    motherFields() {
      return motherFieldsConfig
    },

    guardianFields() {
      return guardianFieldsConfig
    },

    correspondenceFields() {
      return correspondenceFieldsConfig
    },

    billingFields() {
      return billingFieldsConfig
    },

    referralFields() {
      return referralFieldsConfig
    },
  },

  async mounted() {
    window.V = this
    await this.initializeComponent()
  },

  methods: {
    lang,

    initializeForm() {
      return {
        APF_FK_Student_ID: null,
        APF_Surname: null,
        APF_Preferred_Name: null,
        APF_FirstName: null,
        APF_DOB: null,
        APF_POB: null,
        APF_IDType: null,
        APF_IDNumber: null,
        APF_PassportNumber: null,
        APF_PassportExpiryDate: null,
        APF_Gender: null,
        APF_HomeAddress: null,
        APF_Nationality: null,
        APF_CurrentSchool: null,
        APF_CurrentCountry: null,
        APF_EnrolPreviously: null,
        APF_EnrolPreviouslySchool: null,
        APF_IDPASSExpiredDate: null,
        APF_1stLang: null,
        APF_2ndLang: null,
        APF_Emer1Name: null,
        APF_Emer1Relation: null,
        APF_Emer1Tel: null,
        APF_Emer1Mobile: null,
        APF_Emer2Name: null,
        APF_Emer2Relation: null,
        APF_Emer2Tel: null,
        APF_Emer2Mobile: null,
        APF_CorrResidingWith: null,
        APF_CorrAddress: null,
        APF_CorrPostal: null,
        APF_CorrCountry: null,
        APF_BillingName: null,
        APF_BillingAddress: null,
        APF_BillingPostal: null,
        APF_BillingCountry: null,
        APF_BillingEmail: null,
        APF_BillingTel: null,
        APF_Comment1: null,
        APF_Comment2: null,
        APF_Comment3: null,
        APF_ReferralAgentName: null,
        APF_ReferralAgentMobile: null,
        APF_ReferralAgentEmail: null,
        APF_ReferralAgentTel: null,
        APF_ReferralCompany: null,
        // Parents/Guardians
        APFP_FatherFirstName: null,
        APFP_FatherLastName: null,
        APFP_FatherNationality: null,
        APFP_FatherResidencyDocNo: null,
        APFP_FatherOccupation: null,
        APFP_FatherCompanyName: null,
        APFP_FatherMobile: null,
        APFP_FatherHomeTel: null,
        APFP_FatherPersonalEmail: null,
        APFP_FatherMiddleName: null,
        APFP_FatherResidencyDocExpiry: null,
        APFP_MotherFirstName: null,
        APFP_MotherLastName: null,
        APFP_MotherNationality: null,
        APFP_MotherResidencyDocNo: null,
        APFP_MotherOccupation: null,
        APFP_MotherCompanyName: null,
        APFP_MotherMobile: null,
        APFP_MotherHomeTel: null,
        APFP_MotherPersonalEmail: null,
        APFP_MotherMiddleName: null,
        APFP_MotherResidencyDocExpiry: null,
        APFP_LegalGuardianName: null,
        APFP_LegalGuardianEmail: null,
        APFP_LegalGuardianMobile: null,
        APFP_LegalGuardianHomeTel: null,
        APFP_PrimaryParent: null,
        APF_CorrPrimaryContact: null,
        // Questions and answers
        answers: [],
        siblings: [],
      }
    },

    async initializeComponent() {
      if (this.$route.query.id) {
        const apfId = this.$route.query.id
        this.isPreschool = isPreschoolApplication(getSchoolSession())
        await Promise.all([this.getApplicationDetail(apfId), this.getApproversDetail(apfId), this.loadSchoolList()])
        await Promise.all([this.loadParentLiaisonList(), this.loadAcademicYear()])
      }
    },

    async loadSchoolList() {
      this.schoolList = (await DataSource.shared.getSchoolDropdownList('UTY201300000061'))?.Table || []
    },

    async loadParentLiaisonList() {
      this.parentLiaisonList =
        (await DataSource.shared.getUsers({
          CONSchool: this.form.APF_ApplicationSchool,
          CONType: 'Parent Liaison',
        })) || []
    },

    async loadAcademicYear() {
      this.academicYear =
        (await DataSource.shared.getAutoAssignLevelAcademicYear(this.form.APF_CommencementDate, this.form.APF_DOB, this.form.APF_ApplicationSchool))
          ?.Table?.[0] || {}
    },

    async getApplicationDetail(apfId) {
      try {
        const resp = await DataSource.shared.getApplicationFormDetail(apfId)
        this.listCount = resp.FormTable.length

        if (resp && resp.code !== '99') {
          this.processApplicationData(resp)
        }
      } catch (e) {
        console.error('Error fetching application details:', e)
      }
    },

    async sendNotification() {
      try {
        const resp = await DataSource.shared.sendApplicationFormNotifications(this.applicationFormID)
        if (resp?.code === '1') {
          this.$notify.success({
            title: 'Notification',
            message: 'Notification to Principal sent successfully.',
          })
        } else {
          this.$notify.error({
            title: 'Notification',
            message: 'Failed to send notification to Principal',
          })
        }
      } catch (e) {
        console.error('Error sending application notification:', e)
        this.$notify.error({
          title: 'Notification',
          message: 'Failed to send notification',
        })
      }
    },

    processApplicationData(resp) {
      // Process attachments
      this.processAttachments(resp.AttachTable || [])

      // Process form data
      if (resp.FormTable?.length > 0) {
        this.processFormData(resp.FormTable[0])
      }

      // Process answers and siblings
      this.form.answers = resp.AnswerTable || []
      this.form.siblings = (resp.SiblingTable || []).map(s => ({
        ...s,
        APS_DOB: s.APS_DOB?.slice(0, 10),
      }))

      // Process other data
      this.StudentIndexNumber = resp.Others?.StudentNumber || ''
      this.form.APF_FamilyNumber = resp.FormTable?.APF_FamilyNumber || resp.Others?.FamilyNumber
    },

    processAttachments(attachments) {
      attachments.forEach(attachment => {
        if (attachment.APAT_UploadType === 'stuIMG') {
          this.imageUrl = attachment.APAT_FileUrl
        }
      })
      this.attachmentList = attachments
    },

    processFormData(formData) {
      this.applicationFormID = formData.APF_ID
      this.currentAPFID = formData.FK_APF_ID
      this.enrolStatus = formData.APF_Status

      const dateFields = [
        'APF_DOB',
        'APF_PassportExpiryDate',
        'APF_CommencementDate',
        'APF_IDPASSExpiredDate',
        'APF_IDPASSIssueDate',
        'APFP_FatherResidencyDocExpiry',
        'APFP_FatherResidencyDocIssueDate',
        'APFP_MotherResidencyDocExpiry',
        'APFP_MotherResidencyDocIssueDate',
      ]

      Object.keys(formData).forEach(key => {
        if (dateFields.includes(key) && formData[key]) {
          this.form[key] = formData[key].slice(0, 10)
        } else {
          this.form[key] = formData[key]
        }
      })

      this.processBillingName()
    },

    processBillingName() {
      const billingNameMap = {
        faCompany: this.form?.APFP_FatherCompanyName,
        maCompany: this.form?.APFP_MotherCompanyName,
        father: this.form?.APFP_FatherFirstName,
        mother: this.form?.APFP_MotherFirstName,
      }

      if (billingNameMap[this.form?.APF_BillingName]) {
        this.form.APF_BillingName = billingNameMap[this.form.APF_BillingName]
      }
    },

    async getApproversDetail(apfId = null) {
      this.approverList = await DataSource.shared.callWebService(`/controller/Api.asmx/GetApproversOverview`, { APF_ID: apfId || this.currentAPFID }, 'POST')
    },

    approveClick(mode) {
      this.$prompt('Please input remark (Optional)\r\n', mode.toString(), {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
      })
        .then(({ value }) => {
          this.processApprovalAction(mode, value)
        })
        .catch(() => {
          this.$message({ type: 'info', message: 'Canceled' })
        })
    },

    rejectClick(mode) {
      this.$prompt('Please input remark(Mandatory)', mode.toString(), {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
      }).then(({ value }) => {
        if (!value) {
          this.$notify.error({
            title: 'Mandatory Field',
            message: 'Please input remark',
            type: 'Error',
          })
        } else {
          this.processApprovalAction(mode, value)
        }
      })
    },

    async processApprovalAction(mode, remark) {
      this.isApprovalActionInProgress = true
      this.$vs.loading()
      try {
        const result = await this.approveRejectApplicationForm(mode, remark)
        this.handleApprovalResult(result, mode)
        await this.getApproversDetail()
      } catch (error) {
        console.error('Approval action failed:', error)
        this.$notify.error({ title: 'Error', message: 'Action failed' })
      } finally {
        this.$vs.loading.close()
        this.isApprovalActionInProgress = false
      }
    },

    handleApprovalResult(result, mode) {
      const messages = {
        Approve: { success: 'Approved!', error: 'Approve Failed' },
        Reject: { success: 'Rejected!', error: 'Reject Failed' },
      }

      if (result?.code === '1') {
        this.$notify.success({
          title: 'Success',
          message: messages[mode].success,
          type: 'success',
          duration: 2000,
        })
        this.getApplicationDetail(this.currentAPFID)
      } else if (result?.code === '99') {
        this.$notify.error({
          title: 'Approval failed.',
          message: result?.message,
          type: 'error',
        })
      } else if (result?.code === '3') {
        this.$notify.error({
          title: messages[mode].error,
          message: 'Status not valid!',
          type: 'error',
        })
      }
    },

    deleteClick() {
      this.$confirm('Confirm archiving this application?', 'Archive', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(async () => {
        this.isApprovalActionInProgress = true
        this.$vs.loading()
        try {
          await this.approveRejectApplicationForm('Delete', '')
          this.$notify({
            title: 'Archived',
            message: 'Application Archived!',
            type: 'success',
          })
        } catch (error) {
          this.$message({ type: 'error', message: 'Error archiving application' })
        } finally {
          this.$vs.loading.close()
          this.isApprovalActionInProgress = false
        }
      })
    },

    async approveRejectApplicationForm(mode, reason) {
      return await DataSource.shared.approveOrRejectApplicationForm(this.currentAPMD_ID, this.currentAPFID, mode, reason, {
        APF_Comment1: this.form.APF_Comment1,
        APF_Comment2: this.form.APF_Comment2,
        APF_Comment3: this.form.APF_Comment3,
      })
    },

    async handleFileUpload(inputFiles, uploadType) {
      const files = []
      for await (let file of inputFiles) {
        if (file.size / 1024 / 1024 > 10) {
          this.$notify.error({ title: 'Error', message: 'File limit exceeded 10MB' })
          continue
        }
        files.push(file)
      }
      try {
        this.$vs.loading()
        if (files.length > 0) {
          await DataSource.shared.saveApplicationAttachmentToExistingForm(this.currentAPFID, files, uploadType)
          location.reload(true)
        } else {
          this.$notify.error({ title: 'Error', message: 'No Files Selected' })
        }
      } finally {
        this.$vs.loading.close()
      }
    },

    async saveForm() {
      this.$vs.loading()
      try {
        await DataSource.shared.callWebService('/controller/Api.asmx/UpdateApplicationForm', this.form, 'POST')
        window.location.reload()
      } catch (error) {
        console.error('Save failed:', error)
        this.$notify.error({ title: 'Error', message: 'Save failed' })
      } finally {
        this.$vs.loading.close()
      }
    },

    // Commencement date must fall within the semester the student will be assigned to
    validateCommencementDate() {
      const commencementDate = this.form.APF_CommencementDate?.slice(0, 10)
      const semesterFrom = this.academicYear.semesterFrom?.slice(0, 10)
      const semesterTo = this.academicYear.semesterTo?.slice(0, 10)
      const formatDate = date => date.split('-').reverse().join('/')

      if (!commencementDate) {
        return 'Please input the Commencement Date.'
      }
      if (!semesterFrom || !semesterTo) {
        return 'No semester found for this school. Unable to validate the Commencement Date.'
      }
      if (commencementDate < semesterFrom || commencementDate > semesterTo) {
        return `Commencement Date (${formatDate(commencementDate)}) must be within the semester ${this.academicYear.academicYear || ''} (${formatDate(semesterFrom)} - ${formatDate(semesterTo)}).`
      }
      return null
    },

    async createStudent() {
      this.$vs.loading()
      try {
        await this.loadAcademicYear()
        const error = this.validateCommencementDate()
        if (error) {
          this.$notify.error({ title: 'Invalid Commencement Date', message: error })
          return
        }

        await DataSource.shared.CreateStudentFromApplicationForm(this.currentAPFID)
        window.location.reload()
      } catch (error) {
        console.error('Create student failed:', error)
        this.$notify.error({ title: 'Error', message: 'Create student failed' })
      } finally {
        this.$vs.loading.close()
      }
    },

    async deleteStudent() {
      if (confirm('Are you sure you want to delete the student profile?')) {
        this.$vs.loading()
        try {
          await DataSource.shared.DeleteStudentFromApplicationForm(this.form.APF_FK_Student_ID)
          window.location.reload()
        } catch (error) {
          console.error('Delete student failed:', error)
          this.$notify.error({ title: 'Error', message: 'Delete student failed' })
        } finally {
          this.$vs.loading.close()
        }
      }
    },

    async requestApplicantEdits() {
      const payload = {
        APF_ID: this.form.APF_ID,
        comment: this.requestEditComment,
      }

      this.$vs.loading()
      try {
        await DataSource.shared.callWebService('/controller/Api.asmx/RequestApplicantToUpdateApplicationForm', payload, 'POST')
        this.$notify.success({ title: 'Success', message: 'Email Scheduled', type: 'success', duration: 3000 })
      } catch (error) {
        console.error('Request edit failed:', error)
        this.$notify.error({ title: 'Error', message: 'Request failed' })
      } finally {
        this.$vs.loading.close()
      }
    },

    handleApproveRejection(remark) {
      this.processRejectionAction('ApproveRejection', remark)
    },

    handleDeclineRejection(remark) {
      this.processRejectionAction('DeclineRejection', remark)
    },

    async processRejectionAction(mode, remark) {
      this.$vs.loading()
      try {
        const result = await this.approveRejectApplicationForm(mode, remark || '')
        this.handleRejectionActionResult(result, mode)
        await this.getApproversDetail()
      } catch (error) {
        console.error('Rejection action failed:', error)
        this.$notify.error({ title: 'Error', message: 'Action failed' })
      } finally {
        this.$vs.loading.close()
      }
    },

    handleRejectionActionResult(result, mode) {
      const messages = {
        ApproveRejection: { success: 'Application Closed!', error: 'Close application failed' },
        DeclineRejection: { success: 'Application Reopened!', error: 'Reopen application failed' },
      }

      if (result?.code === '1') {
        this.$notify.success({
          title: 'Success',
          message: messages[mode].success,
          type: 'success',
          duration: 2000,
        })
        this.getApplicationDetail(this.currentAPFID)
      } else if (result?.code === '3') {
        this.$notify.error({
          title: messages[mode].error,
          message: 'Status not valid!',
          type: 'error',
        })
      }
    },

    confirmReject(reason) {
      this.processApprovalAction('Reject', reason || '')
    },

    confirmApprove(reason) {
      this.processApprovalAction('Approve', reason || '')
    },
  },
}
</script>

<style scoped>
.container {
  text-align: left;
}

.header-section {
  margin: 20px 0;
}

.page-title {
  font-size: 24px;
  margin: 0;
}

.application-content {
  display: flex;
  gap: 20px;
  margin: 20px 0;
}

.application-main-card {
  background: #f8f9fa;
  border-radius: 6px;
  min-height: 100vh;
  flex: 1;
  padding: 20px;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

@media (max-width: 768px) {
  .application-content {
    flex-direction: column;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
#applicationManagement {
  .form-control:disabled,
  .form-control[readonly] {
    color: #555 !important;
  }
}
</style>
